/**
 * <cba-scratch-popup> — the scratch-card signup popup and its re-entry tab
 * (snippets/cba-scratch-popup). Mounted once per page, from the footer.
 *
 *   <cba-scratch-popup>
 *     <cba-dialog name="scratch"><dialog data-scratch-popup …>…</dialog></cba-dialog>
 *     <div data-scratch-tab>…</div>
 *   </cba-scratch-popup>
 *
 * An ES module and a custom element (DESIGN-PLAN 7.G6, shell audit S18.2):
 * Klaviyo is @theme/klaviyo's, imported, where it was a global on window;
 * and every listener and the trigger's timer end when the element leaves
 * the page. The flow inside start() is the script as it was (plan 6.1j), so the
 * timing test's groups and events are what they were.
 *
 * - Steps: scratch the card, claim, email (Klaviyo), optional SMS with the
 *   self-hosted intl-tel-input picker (asset URLs in data-iti-* on the popup),
 *   then the check-your-inbox step.
 * - When it opens: Theme settings > CBA scratch popup (data-timing and the
 *   data-delay/-scroll/-pages/-seconds on the popup; plan 3.10). In a test
 *   each browser keeps group A or B, and the funnel events and the signup's
 *   profile carry the test and the group.
 * - A re-entry tab on the left edge reopens it at the step the visitor left.
 * - ?scratch=1 opens it at once with tracking off, for QA.
 * - Opening, closing, Escape and the scroll lock are the shared <cba-dialog>'s.
 * - Texts come from data attributes on the popup: its settings (the copy)
 *   and cba.scratch.* / cba.forms.* locale keys (plan 6.2d).
 */
import { track, subscribe, validEmail, isKnown, attribution } from '@theme/klaviyo';

/* The whole flow for one element. `signal` ends every listener and the
   trigger's timer when the element leaves the page. */
function start(el, signal) {
  var root    = el.querySelector('[data-scratch-popup]');
  if (!root) return;
  /* Every listener goes with the signal. */
  function on(target, type, fn, options) {
    var all = { signal: signal }, key;
    if (options) for (key in options) all[key] = options[key];
    target.addEventListener(type, fn, all);
  }
  /* Texts: the popup's settings and locale keys, as data attributes on the popup (plan 6.2d). */
  var TEXT    = root.dataset;
  /* Opening, closing, the backdrop, Escape and the scroll lock are the
     shared <cba-dialog>'s (plan 3.5). */
  var host    = root.closest('cba-dialog');
  var canvas  = root.querySelector('[data-scratch-canvas]');
  var hint    = root.querySelector('[data-scratch-hint]');
  var cardEl  = root.querySelector('[data-scratch-card]');
  var claim   = root.querySelector('[data-scratch-claim]');
  var heading = root.querySelector('[data-scratch-heading]');
  var subtext = root.querySelector('[data-scratch-subtext]');
  var form    = root.querySelector('[data-scratch-form]');
  var emailEl = root.querySelector('[data-scratch-email]');
  var submit  = root.querySelector('[data-scratch-submit]');
  var msgEl   = root.querySelector('[data-scratch-msg]');
  var doneBtn = root.querySelector('[data-scratch-done]');
  var lottieEl = root.querySelector('[data-scratch-lottie]');
  var video    = root.querySelector('[data-scratch-video]');
  var videoBtn = root.querySelector('[data-scratch-video-btn]');
  var playIcon = root.querySelector('[data-scratch-play-icon]');
  var pauseIcon = root.querySelector('[data-scratch-pause-icon]');
  var steps   = root.querySelectorAll('.cba-scratch-popup__step');
  var tab      = el.querySelector('[data-scratch-tab]');
  var tabOpen  = tab && tab.querySelector('[data-scratch-tab-open]');
  var tabClose = tab && tab.querySelector('[data-scratch-tab-close]');

  var DONE_KEY     = 'cba_scratch_done';
  var SESSION_KEY  = 'cba_scratch_shown_session';
  // Re-entry tab state: which step to reopen at, whether the card was already
  // scratched-through, and whether the gift was actually claimed (tab retires).
  var STEP_KEY     = 'cba_scratch_step';
  var REVEAL_KEY   = 'cba_scratch_revealed';
  var CLAIMED_KEY  = 'cba_scratch_claimed';
  var TAB_DISMISS_KEY = 'cba_scratch_tab_dismissed';
  var REVEAL_AT    = 0.5;
  var currentStep  = 'scratch';
  // ?scratch=1 testing override — set at trigger time. When on, the re-entry tab
  // also bypasses the logged-in-customer suppression so QA (usually logged in)
  // can see it. Real visits keep the isExistingCustomer guard.
  var testMode     = false;
  // Coarse pointer (mobile/tablet) — programmatic input focus pops the on-screen
  // keyboard OVER the step's content (video/headline/consent), hiding it the
  // moment the step appears. Auto-focus is therefore desktop-only; on touch the
  // visitor sees the content first and taps the field themselves.
  var coarseInput  = false;
  try { coarseInput = window.matchMedia('(hover: none), (pointer: coarse)').matches; } catch (e) {}
  // Logged-in customers are existing buyers, not new-signup targets — suppress
  // (mirrors Klaviyo's "don't show to existing profiles"). ?scratch=1 bypasses.
  var isExistingCustomer = root.getAttribute('data-scratch-customer') === '1';

  // Funnel tracking is ON for real visits and OFF in the ?scratch=1 testing
  // override, so QA/demo opens never pollute the Klaviyo funnel stats. Every
  // funnel event goes through fire() so this single flag governs all of them.
  var trackingOn = true;

  /* ---- when it opens (plan 3.10; Theme settings > CBA scratch popup) ---- */
  // A opens after a delay. B waits for interest: the first of a scroll through
  // part of the page, a later page of the visit, or time on the site. Both
  // also open when the mouse leaves the page on a computer. In a test every
  // browser draws A or B, half each, and keeps it for the whole test; the
  // funnel events and the signup's profile carry the test and the group, so
  // the results read per group.
  var TIMING     = root.getAttribute('data-timing') || 'delay';
  var TEST       = TIMING === 'test' ? (root.getAttribute('data-test') || '') : '';
  var DELAY_MS   = setting('data-delay', 8) * 1000;
  var SCROLL_AT  = setting('data-scroll', 50) / 100;
  var PAGES_AT   = setting('data-pages', 2);
  var SECONDS_MS = setting('data-seconds', 25) * 1000;
  var GROUP_KEY  = 'cba_scratch_group';
  function setting(attr, fallback) {
    var n = parseFloat(root.getAttribute(attr));
    return isFinite(n) && n > 0 ? n : fallback;
  }
  function pickGroup() {
    if (TIMING === 'interest') return 'B';
    if (TIMING !== 'test') return 'A';
    try {
      var saved = JSON.parse(localStorage.getItem(GROUP_KEY) || 'null');
      if (saved && saved.test === TEST && (saved.group === 'A' || saved.group === 'B')) return saved.group;
    } catch (e) {}
    var drawn = Math.random() < 0.5 ? 'A' : 'B';
    try { localStorage.setItem(GROUP_KEY, JSON.stringify({ test: TEST, group: drawn })); } catch (e) {}
    return drawn;
  }
  var GROUP = pickGroup();
  var testProps = { popup_group: GROUP };
  if (TEST) testProps.popup_test = TEST;

  // The visit in this tab: when it started and how many pages it has seen.
  var visitStart = Date.now(), visitPages = 1;
  try {
    visitStart = parseInt(sessionStorage.getItem('cba_visit_start'), 10) || visitStart;
    visitPages = (parseInt(sessionStorage.getItem('cba_visit_pages'), 10) || 0) + 1;
    sessionStorage.setItem('cba_visit_start', String(visitStart));
    sessionStorage.setItem('cba_visit_pages', String(visitPages));
  } catch (e) {}

  function fire(metric, props) {
    if (!trackingOn) return;
    var all = {}, key;
    for (key in testProps) all[key] = testProps[key];
    if (props) for (key in props) all[key] = props[key];
    track(metric, all);
  }

  /* ---- suppression ---------------------------------------------------- */
  // Only a completed SIGNUP (email captured → DONE_KEY) suppresses the auto-popup
  // across sessions. A visitor who dismissed WITHOUT signing up is NOT snoozed —
  // the auto-popup returns every NEW session (bounded by the per-session guard).
  // The re-entry tab still covers within-session / signed-up-not-claimed re-entry.
  function suppressed() {
    try {
      if (localStorage.getItem(DONE_KEY) === '1') return true;
    } catch (e) {}
    return false;
  }
  function markDone() {
    try { localStorage.setItem(DONE_KEY, '1'); } catch (e) {}
  }
  // Auto-show at most once per browser session (sessionStorage) so the popup
  // doesn't re-appear — and re-fire "Viewed" — on every page within a session.
  // A NEW session shows it again (unless the visitor signed up → DONE_KEY).
  function shownThisSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
  }
  // Gift actually added to cart — the journey is complete; the tab retires for good.
  function claimed() {
    try { return localStorage.getItem(CLAIMED_KEY) === '1'; } catch (e) { return false; }
  }
  function persistStep(name) {
    try { localStorage.setItem(STEP_KEY, name); } catch (e) {}
  }

  /* ---- re-entry tab --------------------------------------------------- */
  // A soft re-entry surfaced whenever the popup is closed before the gift is
  // claimed. Never shown to already-claimed visitors, to those who explicitly
  // dismissed the tab (×), or to logged-in customers (except in ?scratch=1 QA).
  function tabDismissed() {
    try { return localStorage.getItem(TAB_DISMISS_KEY) === '1'; } catch (e) { return false; }
  }
  function showTab() {
    if (!tab || claimed() || tabDismissed() || (isExistingCustomer && !testMode)) return;
    tab.classList.add('is-visible');
    tab.removeAttribute('aria-hidden');
    if (tabOpen)  tabOpen.removeAttribute('tabindex');
    if (tabClose) tabClose.removeAttribute('tabindex');
  }
  function hideTab() {
    if (!tab) return;
    tab.classList.remove('is-visible');
    tab.setAttribute('aria-hidden', 'true');
    if (tabOpen)  tabOpen.setAttribute('tabindex', '-1');
    if (tabClose) tabClose.setAttribute('tabindex', '-1');
  }
  // × — the visitor really doesn't want the gift; retire the tab (persisted).
  function dismissTab() {
    try { localStorage.setItem(TAB_DISMISS_KEY, '1'); } catch (e) {}
    hideTab();
  }

  /* ---- open / close --------------------------------------------------- */
  var opened = false;
  // The raw DOM open — shared by the first auto-open and every tab re-entry.
  function openDom() {
    customElements.whenDefined('cba-dialog').then(function () {
      host.open();
      hideTab();                 // popup is up — retract the re-entry tab
      persistStep(currentStep);  // remember the step across pages from the moment it opens
      initCanvas();
    });
  }
  /* The automatic open never interrupts: while a drawer or another popup is
     open it waits until the last one closes. */
  var waiting = false;
  function whenNoDialog(fn) {
    if (!document.querySelector('dialog:modal')) { fn(); return; }
    if (waiting) return;
    waiting = true;
    on(document, 'cba-dialog:close', function onClose() {
      if (document.querySelector('dialog:modal')) return;
      document.removeEventListener('cba-dialog:close', onClose);
      waiting = false;
      fn();
    });
  }
  function reallyOpen() {
    if (document.querySelector('dialog:modal')) { whenNoDialog(reallyOpen); return; }
    openDom();
    if (opened) return;        // fire the funnel-entry + session mark once per page
    opened = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
    // Funnel entry — popup shown (carry UTM/referrer on the entry event).
    fire('Viewed Scratch Popup', attribution());
  }
  // Tab re-entry: restore the saved step, open, and log a DISTINCT event so the
  // funnel's "Viewed" count stays clean (this is a deliberate re-open, not a view).
  function reopenFromTab() {
    restoreStep();
    openDom();
    fire('Reopened Scratch Popup', { step: currentStep });
  }
  // Restore the popup to the step the visitor last reached (persisted across pages).
  function restoreStep() {
    var saved = 'scratch', wasRevealed = false;
    try {
      saved = localStorage.getItem(STEP_KEY) || 'scratch';
      wasRevealed = localStorage.getItem(REVEAL_KEY) === '1';
    } catch (e) {}
    currentStep = saved;
    showStep(saved);
    if (saved === 'scratch' && wasRevealed) {
      // Already scratched-through last time — show the revealed card (prize +
      // Claim button) and stop initCanvas from repainting the foil over it.
      sized = true;
      revealed = true;
      if (canvas) { canvas.style.pointerEvents = 'none'; canvas.style.visibility = 'hidden'; }
      if (heading) heading.textContent = TEXT.revealedHeading;
      if (subtext) subtext.textContent = TEXT.revealedSubtext;
      root.classList.add('is-revealed');
    } else if (saved === 'email') {
      playGiftVideo();
      initIti();
    } else if (saved === 'sms') {
      initIti();
    }
  }
  // Gate: skip if dismissed/converted on this device (localStorage), already
  // shown this session, OR already a known Klaviyo profile (best-effort).
  function open() {
    if (opened || suppressed() || shownThisSession() || isExistingCustomer) return;
    // isKnown() is a best-effort NICETY (skip already-known profiles). It must
    // NEVER block the popup: klaviyo.isIdentified() can stay pending indefinitely
    // (verified 2026-07-01 — it never resolved on live OR preview), which with the
    // old code hid the popup from every visitor. Race it against a short timeout
    // and DEGRADE TO SHOWING — the documented intent, now actually implemented.
    var settled = false;
    function decide(known) { if (settled) return; settled = true; if (!known) reallyOpen(); }
    var t = setTimeout(function () { decide(false); }, 1200);
    isKnown().then(
      function (known) { clearTimeout(t); decide(known); },
      function () { clearTimeout(t); decide(false); }
    );
  }
  /* close(true) after the gift is claimed; the X, Escape and the backdrop
     close through <cba-dialog> as a plain close. Either way the tab logic
     runs once the popup has closed. */
  var closingViaConvert = false;
  function close(viaConvert) {
    closingViaConvert = !!viaConvert;
    host.close();
  }
  on(host, 'cba-dialog:close', function (e) {
    if (e.target !== host) return;
    var viaConvert = closingViaConvert;
    closingViaConvert = false;
    if (viaConvert) {
      // Gift claimed — retire the tab for good.
      try { localStorage.setItem(CLAIMED_KEY, '1'); } catch (e) {}
      hideTab();
    } else {
      // Closed before claiming — NO cross-session snooze (the auto-popup returns
      // every new session; the per-session guard stops it re-opening THIS session).
      // Leave a soft re-entry via the tab, reopening at the step they left off.
      showTab();
    }
  });

  if (tabOpen)  on(tabOpen, 'click', reopenFromTab);
  if (tabClose) on(tabClose, 'click', dismissTab);

  /* ---- step navigation ------------------------------------------------ */
  function showStep(name) {
    steps.forEach(function (s) { s.hidden = (s.getAttribute('data-step') !== name); });
  }
  // Advance to a step AND remember it, so the re-entry tab can reopen here.
  function setStep(name) {
    currentStep = name;
    showStep(name);
    persistStep(name);
  }

  /* ---- scratch canvas ------------------------------------------------- */
  var ctx, revealed = false, drawing = false, sized = false, eraseFill, moveCount = 0;
  function readToken(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }
  function initCanvas() {
    if (sized || !canvas || !cardEl) return;
    var rect = cardEl.getBoundingClientRect();
    if (!rect.width) return;
    var dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    // Opaque source for the destination-out erase (alpha is what matters).
    eraseFill = 'rgb(' + readToken('--cba-primary-rgb') + ')';
    // Foil — calm two-stop gradient from brand tokens (no hardcoded color).
    var grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    grad.addColorStop(0, readToken('--cba-lavender'));
    grad.addColorStop(1, readToken('--cba-surface'));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);
    // "SCRATCH HERE" hint
    ctx.fillStyle = 'rgba(' + readToken('--cba-primary-rgb') + ', 0.4)';
    ctx.font = '600 ' + (rect.width * 0.09) + 'px Poppins, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(TEXT.canvasHint || '', rect.width / 2, rect.height / 2);
    sized = true;
  }
  function pointFromEvent(e) {
    var rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, w: rect.width };
  }
  function scratchAt(p) {
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = eraseFill;
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(14, p.w * 0.08), 0, Math.PI * 2);
    ctx.fill();
  }
  function clearedFraction() {
    if (!ctx || !canvas.width) return 0;
    var img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    var cleared = 0, total = img.length / 4;
    for (var i = 3; i < img.length; i += 4 * 16) { if (img[i] === 0) cleared++; }
    return cleared / (total / 16);
  }
  function maybeReveal() {
    if (!revealed && clearedFraction() >= REVEAL_AT) reveal(false);
  }
  /* The card is revealed by scratching enough of it, or at once by a tap,
     Enter or Space (WCAG 2.1.1 and 2.5.7: a keyboard and a single tap must
     do what the drag does). A reveal by key moves focus to "Claim gift". */
  function reveal(byKey) {
    if (revealed) return;
    revealed = true;
    // Persist the reveal so a tab re-entry restores the scratched-through card.
    try { localStorage.setItem(REVEAL_KEY, '1'); } catch (e) {}
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.style.pointerEvents = 'none';
    canvas.setAttribute('tabindex', '-1');
    canvas.setAttribute('aria-hidden', 'true');
    // Reveal IN PLACE — same card + layout, just swap the copy and celebrate.
    // .is-revealed shows the (space-reserved) Claim button + fires the burst.
    if (heading) heading.textContent = TEXT.revealedHeading;
    if (subtext) subtext.textContent = TEXT.revealedSubtext;
    root.classList.add('is-revealed');
    playLottie();
    // Funnel: prize revealed (step 1 complete).
    fire('Revealed Scratch Prize', { popup: 'scratch' });
    if (byKey && claim) requestAnimationFrame(function () { claim.focus(); });
  }

  /* ---- reveal celebration: self-hosted Lottie (lazy) ------------------- */
  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  var lottieReq = false, lottieData = null, lottieAnim = null;
  // Load the player + animation only once the user actually starts scratching,
  // so the celebration never touches initial page load (perf budget).
  function preloadLottie() {
    if (lottieReq || reduceMotion || !lottieEl) return;
    lottieReq = true;
    var src = lottieEl.getAttribute('data-lottie-src');
    var js  = lottieEl.getAttribute('data-lottie-js');
    if (src) fetch(src).then(function (r) { return r.json(); }).then(function (d) { lottieData = d; }).catch(function () {});
    if (js && !window.lottie) {
      var s = document.createElement('script');
      s.src = js; s.async = true;
      document.head.appendChild(s);
    }
  }
  function playLottie() {
    if (reduceMotion || !lottieEl || lottieAnim) return;
    var tries = 0;
    (function go() {
      if (window.lottie && lottieData) {
        lottieAnim = window.lottie.loadAnimation({
          container: lottieEl, renderer: 'svg', loop: false, autoplay: true,
          animationData: lottieData,
          rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
        });
        lottieAnim.addEventListener('complete', function () { if (lottieEl) lottieEl.classList.add('is-done'); });
      } else if (tries++ < 60) {
        setTimeout(go, 80);   // wait up to ~5s for the lazy script/data
      }
    })();
  }

  if (canvas) {
    var tapStart = null;
    on(canvas, 'pointerdown', function (e) {
      drawing = true;
      tapStart = { x: e.clientX, y: e.clientY };
      if (hint) hint.hidden = true;
      preloadLottie();   // lazy-load the celebration once the user engages
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      scratchAt(pointFromEvent(e));
    });
    // Reveal DURING the drag (throttled — getImageData is expensive), not
    // only on release.
    on(canvas, 'pointermove', function (e) {
      if (!drawing) return;
      scratchAt(pointFromEvent(e));
      if ((++moveCount % 5) === 0) maybeReveal();
    });
    on(canvas, 'pointerup', function (e) {
      drawing = false;
      // A tap (the pointer barely moved) reveals the card at once.
      if (tapStart && Math.abs(e.clientX - tapStart.x) < 10 && Math.abs(e.clientY - tapStart.y) < 10) { tapStart = null; reveal(false); return; }
      tapStart = null;
      maybeReveal();
    });
    on(canvas, 'keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      if (hint) hint.hidden = true;
      preloadLottie();
      reveal(true);
    });
    on(canvas, 'pointerleave', function () { if (drawing) { drawing = false; maybeReveal(); } });
  }

  /* ---- email-step product video: play/pause toggle (PDP gallery pattern) ---- */
  function updateVideoIcon() {
    if (!video || !playIcon || !pauseIcon) return;
    playIcon.style.display  = video.paused ? '' : 'none';
    pauseIcon.style.display = video.paused ? 'none' : '';
  }
  function toggleVideo() {
    if (!video) return;
    if (video.paused) video.play().catch(function () {});
    else video.pause();
    updateVideoIcon();
  }
  if (video) {
    on(video, 'click', toggleVideo);
    if (videoBtn) on(videoBtn, 'click', toggleVideo);
    on(video, 'play', updateVideoIcon);
    on(video, 'pause', updateVideoIcon);
    updateVideoIcon();
  }
  // Start the muted loop when the email step appears — preload="none" keeps the
  // video off the wire until the visitor actually reaches this step.
  function playGiftVideo() { if (video) { video.play().catch(function () {}); updateVideoIcon(); } }

  on(claim, 'click', function () {
    setStep('email'); playGiftVideo();
    if (!coarseInput && emailEl) emailEl.focus();   // desktop only — no keyboard-over-content on mobile
    initIti();   // preload the phone picker now, so it's ready by the SMS step
    // Funnel: clicked through to the email step (step 2).
    fire('Clicked Claim Gift', { popup: 'scratch' });
  });

  /* ---- email submit → Klaviyo → gift step ----------------------------- */
  /* An error stays until the address is changed (it no longer vanishes after
     5 s), and the field is marked invalid and points at it (WCAG 3.3.1). */
  function markField(field, msg, text) {
    if (!field) return;
    if (text) { field.setAttribute('aria-invalid', 'true'); field.setAttribute('aria-describedby', msg.id); }
    else { field.removeAttribute('aria-invalid'); field.removeAttribute('aria-describedby'); }
  }
  function showMsg(text) {
    if (!msgEl) return;
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    markField(emailEl, msgEl, text);
  }

  // Gate on a real email FORMAT only (validEmail is @theme/klaviyo's, so the
  // footer form and this popup accept the same addresses). Consent is given by
  // submitting the form (the notice under the button states the marketing-email
  // purpose + policy).
  function refreshSubmitState() {
    if (submit) submit.disabled = !validEmail(emailEl && emailEl.value);
  }
  if (emailEl) on(emailEl, 'input', function () { refreshSubmitState(); showMsg(''); });

  on(form, 'submit', function (e) {
    e.preventDefault();
    var email = (emailEl.value || '').trim();
    if (!validEmail(email)) { showMsg(TEXT.emailInvalid); return; }
    submit.disabled = true;
    showMsg('');

    // Subscribe (email marketing consent + list -> welcome flow). The helper
    // also identifies the browser so the earlier anonymous step-events stitch
    // onto this profile.
    subscribe(email, { source: 'Scratch popup', properties: testProps })
    .then(function (res) {
      if (!res.ok) throw new Error('subscribe failed');
      capturedEmail = email;   // remember it — the optional SMS step links to this profile
      markDone();              // email captured = converted; don't re-show
      fire('Scratch Popup Subscribed', { popup: 'scratch' });
      showSmsStep();           // optional SMS step next (skippable → gift)
    })
    .catch(function () {
      submit.disabled = false;
      showMsg(TEXT.error);
    });
  });

  /* ---- SMS step (optional): phone → SMS consent → gift ---------------- */
  // Shown AFTER email is already subscribed, so a failed/declined SMS step can
  // never lose the signup — the gift is always reachable via "Skip this step".
  var capturedEmail = '';
  var phoneEl   = root.querySelector('[data-scratch-phone]');
  var smsForm   = root.querySelector('[data-scratch-sms-form]');
  var smsSubmit = root.querySelector('[data-scratch-sms-submit]');
  var smsSkip   = root.querySelector('[data-scratch-sms-skip]');
  var smsMsg    = root.querySelector('[data-scratch-sms-msg]');

  function showSmsStep() {
    initIti();   // lazy-load the international phone picker
    setStep('sms');
    fire('Viewed SMS Step', { popup: 'scratch' });
    if (!coarseInput && phoneEl) phoneEl.focus();   // desktop only — no keyboard-over-content on mobile
  }
  function smsShowMsg(t, info) {
    if (!smsMsg) return;
    smsMsg.textContent = t || '';
    smsMsg.hidden = !t;
    markField(phoneEl, smsMsg, info ? '' : t); // "One moment…" is not an error
  }
  if (phoneEl) on(phoneEl, 'input', function () { smsShowMsg(''); });

  // intl-tel-input v24, the "WithUtils" bundle — validation/formatting is baked in,
  // so there's NO separate utils module to load (the plain build + loadUtils import
  // resolved but never wired up validation → every number got rejected). Lazy-loaded
  // the first time the SMS step opens. Gives a SEARCHABLE country dropdown + flags.
  // Degrades to a plain tel input if the script fails. Self-hosted, pinned at 24.8.2
  // (assets/vendor-intl-tel-input.*, provenance in the CSS header): no third-party
  // request before consent (THEME-STANDARD §8.3–8.4).
  var itiInstance = null, itiReq = false, itiFailed = false;
  function initIti() {
    if (itiReq || !phoneEl) return;
    itiReq = true;
    // The library CSS reads its sprite paths from these variables; versioned
    // asset URLs (data-iti-* on the popup) instead of its relative paths, so a
    // CDN copy is never stale.
    var rootStyle = document.documentElement.style;
    rootStyle.setProperty('--iti-path-flags-1x', 'url(' + root.getAttribute('data-iti-flags') + ')');
    rootStyle.setProperty('--iti-path-flags-2x', 'url(' + root.getAttribute('data-iti-flags-2x') + ')');
    rootStyle.setProperty('--iti-path-globe-1x', 'url(' + root.getAttribute('data-iti-globe') + ')');
    rootStyle.setProperty('--iti-path-globe-2x', 'url(' + root.getAttribute('data-iti-globe-2x') + ')');
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = root.getAttribute('data-iti-css');
    document.head.appendChild(link);
    var s = document.createElement('script');
    s.src = root.getAttribute('data-iti-js');
    s.async = true;
    s.onerror = function () { itiFailed = true; };
    s.onload = function () {
      try {
        if (!window.intlTelInput || !phoneEl) { itiFailed = true; return; }
        /* The country list opens inside the popup: the popup is a modal
           <dialog> in the top layer, and a list appended to <body> (or the
           phone-sized full-screen list, which also goes to <body>) sat behind
           it, so no other country could be picked (logic audit S10.3). The
           first country is the shopper's market, not Sweden for everyone. */
        itiInstance = window.intlTelInput(phoneEl, {
          initialCountry: root.getAttribute('data-country') || 'se',
          separateDialCode: true,   // show the dial code as a chip; user types the national number
          countrySearch: true,      // searchable country dropdown
          strictMode: true,         // only digits + an optional leading + ; cap at max valid length;
                                    // typing "+<code>" switches country, plain digits keep it
          autoPlaceholder: 'off',
          dropdownContainer: null,
          useFullscreenPopup: false
        });
        // Inline feedback: flag an invalid number as soon as the field loses focus.
        on(phoneEl, 'blur', function () {
          if (!phoneEl.value.trim()) return;
          smsShowMsg(itiInstance.isValidNumber() ? '' : TEXT.phoneInvalid);
        });
        on(phoneEl, 'input', function () { smsShowMsg(''); });
      } catch (e) { itiFailed = true; }
    };
    document.head.appendChild(s);
  }

  if (smsSkip) on(smsSkip, 'click', function () {
    fire('Skipped SMS Step', { popup: 'scratch' });
    enterGift();
  });

  if (smsForm) on(smsForm, 'submit', function (e) {
    e.preventDefault();
    var phone = '';
    if (itiInstance) {
      if (!itiInstance.isValidNumber()) { smsShowMsg(TEXT.phoneInvalid); return; }
      phone = itiInstance.getNumber();   // full E.164, any country
    } else if (itiFailed) {
      // Picker truly failed to load (rare) — best-effort: require a full +country number.
      phone = (phoneEl && phoneEl.value || '').replace(/[^\d+]/g, '');
      if (phone && phone.charAt(0) !== '+') phone = '+' + phone;
      if (!/^\+\d{8,15}$/.test(phone)) { smsShowMsg(TEXT.phoneInvalidCode); return; }
    } else {
      // Picker still loading — don't risk accepting an unvalidated number.
      smsShowMsg(TEXT.oneMoment, true); return;
    }
    if (!capturedEmail) { enterGift(); return; }
    smsSubmit.disabled = true;
    smsShowMsg('');
    // Separate call, fired AFTER email is banked → an SMS region/validation error
    // can't lose the email. Includes the email so Klaviyo links it to one profile.
    subscribe(capturedEmail, { phone: phone, source: 'Scratch popup SMS' })
      .then(function (res) {
        if (!res.ok) throw new Error('sms subscribe failed');
        fire('SMS Subscribed', { popup: 'scratch' });
        enterGift();
      })
      .catch(function () {
        smsSubmit.disabled = false;
        smsShowMsg(TEXT.smsError);
      });
  });

  /* ---- gift step: "check your email" — no on-site add-to-cart ---------- */
  // The gift CODE is delivered by the welcome email (new sender domain being
  // warmed up). Reaching this step means the visitor is already subscribed, so
  // the gift is secured. enterGift() is the single forward entry to the step:
  // it shows the step, retires the re-entry tab (CLAIMED), and fires the
  // funnel's final event once (the old on-site add-to-cart no longer exists).
  var giftEntered = false;
  function enterGift() {
    setStep('gift');
    if (giftEntered) return;
    giftEntered = true;
    try { localStorage.setItem(CLAIMED_KEY, '1'); } catch (e) {}
    fire('Claimed Scratch Gift', { popup: 'scratch', via: 'email' });
  }
  // "Got it" just closes (close(true) already retired the tab via enterGift).
  if (doneBtn) on(doneBtn, 'click', function () { close(true); });

  /* ---- trigger -------------------------------------------------------- */
  // ?scratch=1 — preview/testing override: clear suppression + show now.
  var forceShow = false;
  try { forceShow = new URLSearchParams(location.search).has('scratch'); } catch (e) {}
  if (forceShow) {
    trackingOn = false;   // QA/demo opens must not pollute the Klaviyo funnel stats
    testMode = true;      // let the re-entry tab show even for logged-in QA
    try { localStorage.removeItem(DONE_KEY); localStorage.removeItem(TAB_DISMISS_KEY); } catch (e) {}
    reallyOpen();   // testing override — bypass localStorage + session + isKnown() gates
  } else {
    // Re-entry tab persists across PAGE NAVIGATION: any visitor who is
    // mid-funnel (popup opened at least once → STEP_KEY set) gets the tab on
    // EVERY page — signed up or not — until the gift is claimed or the ×
    // dismisses it (showTab() itself guards claimed/dismissed/logged-in).
    // If the auto-popup opens on top (new session), openDom() retracts the
    // tab while the popup is up and close() brings it back.
    try {
      if (localStorage.getItem(STEP_KEY)) showTab();
    } catch (e) {}
    if (!suppressed()) {
      // Whichever trigger comes first opens it, once.
      var armed = true, timer = null;
      var trigger = function () {
        if (!armed) return;
        armed = false;
        clearTimeout(timer);
        document.removeEventListener('mouseout', onExit);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('load', trigger);
        open();
      };
      var onExit = function (e) { if (e.clientY <= 0) trigger(); };
      var onScroll = function () {
        var page = document.documentElement.scrollHeight;
        if (page && (window.scrollY + window.innerHeight) / page >= SCROLL_AT) trigger();
      };
      on(document, 'mouseout', onExit);
      if (GROUP === 'A') {
        timer = setTimeout(trigger, DELAY_MS);
      } else {
        on(window, 'scroll', onScroll, { passive: true });
        timer = setTimeout(trigger, Math.max(0, SECONDS_MS - (Date.now() - visitStart)));
        if (visitPages >= PAGES_AT) {
          if (document.readyState === 'complete') trigger();
          else on(window, 'load', trigger);
        }
      }
    }
  }
  /* Not through on(): a listener that carries the signal is removed before the signal's own abort event. */
  signal.addEventListener('abort', function () { clearTimeout(timer); });
}

if (!customElements.get('cba-scratch-popup')) {
  customElements.define('cba-scratch-popup', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      start(this, this.abort.signal);
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }
  });
}
